import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {DaysOfWeek} from '../../../../../../constants/general-variables';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BusinessProfilesHttpService} from '../../../../../../services/http-services/business-profiles/business-profiles-http.service';
import {ToastrService} from 'ngx-toastr';
import {GetTermsApiData, GrTermsData} from '../../../../../../services/http-services/business-profiles/business-profiles-http-interfaces';
import {timeRangeValidator} from '../../../../../../validators/form-validators.validator';

@Component({
  selector: 'gr-terms-first-step',
  templateUrl: './terms-first-step.component.html',
})
class TermsFirstStepComponent implements OnInit, OnChanges, AfterViewInit  {
  termsFirstStepForm!: FormGroup;
  formTriedToSubmit = false;
  @Input() termsData!: {isSecondStepChanges: boolean, terms: GrTermsData | null};
  @Input() urlParams: any;
  @Input() sameWorkTime = false;
  @Input() sameSlots = false;
  @Output() submitForm: EventEmitter<void> = new EventEmitter<void>();
  @Output() workingDaysChange: EventEmitter<any> = new EventEmitter<any>();
  isEditTermsReq = false;
  initialCheckedDays: any = [];
  protected readonly daysOfWeek = DaysOfWeek;

  constructor(private formBuilder: FormBuilder,
              private toastrService: ToastrService,
              private businessProfilesHttpService: BusinessProfilesHttpService) {
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.termsData && changes.termsData.currentValue.terms && !this.isEditTermsReq && !changes.termsData.currentValue.isSecondStepChanges) {
      this.patchFormData();
    }
  }

  ngAfterViewInit(): void {
    this.initValueChangeSubscriptions();
  }

  buildForm(): void {
    this.termsFirstStepForm = this.formBuilder.group({
      days: this.formBuilder.array([]),
      everyDaySameWorkingTime: [false],
      workingTimeStart: [''],
      workingTimeEnd: [''],
      everyDaySameSlots: [false],
      timeSlot: [''],
      breakTime: [''],
      individualWorkingTimes: this.formBuilder.array([]),
      individualWorkingSlotsAndBreaks: this.formBuilder.array([]),
    });
  }

  initValueChangeSubscriptions(): void {
    const daysArray = this.termsFirstStepForm.controls.days as FormArray;

    // Provera da li ima dana
    if (daysArray.length === 0) {
      console.warn('Days FormArray je prazan u ngAfterViewInit.');
      return;
    }

    // Subscribe za days
    this.termsFirstStepForm.controls.days.valueChanges.subscribe(val => {
      this.workingDaysChange.emit(val);
    });

    // Subscribe za everyDaySameWorkingTime
    this.termsFirstStepForm.controls.everyDaySameWorkingTime.valueChanges.subscribe(val => {
      const workingTimeStart = this.termsFirstStepForm.get('workingTimeStart');
      const workingTimeEnd = this.termsFirstStepForm.get('workingTimeEnd');
      const individualTimesArray = this.termsFirstStepForm.controls.individualWorkingTimes as FormArray;

      if (val) {
        workingTimeStart?.setValidators([Validators.required, timeRangeValidator]);
        workingTimeEnd?.setValidators([Validators.required, timeRangeValidator]);
        individualTimesArray.clear();
      } else {
        workingTimeStart?.clearValidators();
        workingTimeEnd?.clearValidators();
        individualTimesArray.clear();
        daysArray.controls.forEach(dayControl => {
          individualTimesArray.push(
            this.formBuilder.group({
              day: dayControl.value,
              workingTimeStart: [workingTimeStart?.value, [Validators.required, timeRangeValidator]],
              workingTimeEnd: [workingTimeEnd?.value, [Validators.required, timeRangeValidator]]
            })
          );
        });
      }
      workingTimeStart?.updateValueAndValidity();
      workingTimeEnd?.updateValueAndValidity();
    });

    // Subscribe za everyDaySameSlots
    this.termsFirstStepForm.controls.everyDaySameSlots.valueChanges.subscribe(val => {
      const timeSlot = this.termsFirstStepForm.get('timeSlot');
      const breakTime = this.termsFirstStepForm.get('breakTime');
      const individualWorkingSlotsAndBreaks = this.termsFirstStepForm.controls.individualWorkingSlotsAndBreaks as FormArray;

      if (val) {
        timeSlot?.setValidators([Validators.required]);
        breakTime?.setValidators([Validators.required]);
        individualWorkingSlotsAndBreaks.clear();
      } else {
        timeSlot?.clearValidators();
        breakTime?.clearValidators();
        individualWorkingSlotsAndBreaks.clear();
        daysArray.controls.forEach(dayControl => {
          individualWorkingSlotsAndBreaks.push(
            this.formBuilder.group({
              day: dayControl.value,
              timeSlot: [timeSlot?.value, Validators.required],
              breakTime: [breakTime?.value, Validators.required]
            })
          );
        });
      }
      timeSlot?.updateValueAndValidity();
      breakTime?.updateValueAndValidity();
    });
  }

  patchFormData(): void {
    this.termsFirstStepForm.controls.everyDaySameWorkingTime.patchValue(this.sameWorkTime);
    this.termsFirstStepForm.controls.everyDaySameSlots.patchValue(this.sameSlots);
    this.termsFirstStepForm.controls.workingTimeStart.patchValue(this.termsData.terms?.calendars[0]?.working_time_start.slice(0, -3));
    this.termsFirstStepForm.controls.workingTimeEnd.patchValue(this.termsData.terms?.calendars[0]?.working_time_end.slice(0, -3));
    this.termsData.terms?.calendars.forEach(calendarDate => {
      this.initialCheckedDays.push(calendarDate.day);
      const selectedDays = this.termsFirstStepForm.controls.days as FormArray;
      const individualTimesArray = this.termsFirstStepForm.controls.individualWorkingTimes as FormArray;
      const individualWorkingSlotsAndBreaks = this.termsFirstStepForm.controls.individualWorkingSlotsAndBreaks as FormArray;
      selectedDays.push(new FormControl(calendarDate.day));
      if (!this.termsFirstStepForm.value.everyDaySameWorkingTime) {
        individualTimesArray.push(
          this.formBuilder.group({
            day: calendarDate.day,
            workingTimeStart: [calendarDate.working_time_start.slice(0, -3), [Validators.required, timeRangeValidator]],
            workingTimeEnd: [calendarDate.working_time_end.slice(0, -3), [Validators.required, timeRangeValidator]]
          })
        );
      }
      if (this.termsFirstStepForm.controls.everyDaySameSlots.value) {
        this.termsFirstStepForm.controls.timeSlot.patchValue(this.termsData.terms?.calendars[0].time_slot);
        this.termsFirstStepForm.controls.breakTime.patchValue(this.termsData.terms?.calendars[0].break_time);
      } else {
        individualWorkingSlotsAndBreaks.push(
          this.formBuilder.group({
            day: calendarDate.day,
            timeSlot: [calendarDate.time_slot, Validators.required],
            breakTime: [calendarDate.break_time, Validators.required],
          })
        );
      }
    });
    // Emituj nakon što su dani popunjeni
    this.workingDaysChange.emit(this.termsFirstStepForm.controls.days.value);

  }

  onDaysCheckboxChange(event: any): void {
    const value = event.target.value;
    const checked = event.target.checked;

    const selectedDays = this.termsFirstStepForm.get('days') as FormArray;
    const timesArray = this.termsFirstStepForm.get('individualWorkingTimes') as FormArray;
    const slotsArray = this.termsFirstStepForm.get('individualWorkingSlotsAndBreaks') as FormArray;

    const index = selectedDays.controls.findIndex(c => c.value === value);

    if (checked && index === -1) {
      selectedDays.push(new FormControl(value));
      if (!this.termsFirstStepForm.value.everyDaySameWorkingTime) {
        timesArray.push(this.createWorkingTimeGroup(value));
      }
      if (!this.termsFirstStepForm.value.everyDaySameSlots) {
        slotsArray.push(this.createSlotAndBreakGroup(value));
      }
    } else if (!checked && index !== -1) {
      selectedDays.removeAt(index);
      if (!this.termsFirstStepForm.value.everyDaySameWorkingTime) timesArray.removeAt(index);
      if (!this.termsFirstStepForm.value.everyDaySameSlots) slotsArray.removeAt(index);
    }
    // Emit manually because Angular doesn't detect push/removeAt as valueChanges
    this.workingDaysChange.emit(selectedDays.value);
  }

  createWorkingTimeGroup(day: string, data: any = {}): FormGroup {
    return this.formBuilder.group({
      day: [day],
      workingTimeStart: [data.working_time_start?.slice(0, -3) || '', [Validators.required, timeRangeValidator]],
      workingTimeEnd: [data.working_time_end?.slice(0, -3) || '', [Validators.required, timeRangeValidator]]
    });
  }

  createSlotAndBreakGroup(day: string, data: any = {}): FormGroup {
    return this.formBuilder.group({
      day: [day],
      timeSlot: [data.time_slot || '', Validators.required],
      breakTime: [data.break_time || '', Validators.required],
    });
  }

  get individualWorkingTimes(): FormArray {
    return this.termsFirstStepForm.get('individualWorkingTimes') as FormArray;
  }

  get individualWorkingSlotsAndBreaks(): FormArray {
    return this.termsFirstStepForm.get('individualWorkingSlotsAndBreaks') as FormArray;
  }

  onSubmitTermsFirstStepForm(): void {
    this.formTriedToSubmit = true;
    if (this.termsFirstStepForm.valid) {
      const bodyParams: any = {};
      bodyParams.calendars = [];
      bodyParams.calendars = this.termsFirstStepForm.controls.days.value.map((day: string, index: number) => {
        const dayObj = {
          day,
          working_time_start: '',
          working_time_end: '',
          break_time: '',
          time_slot: '',
        };
        if (this.termsFirstStepForm.controls.everyDaySameWorkingTime.value) {
          dayObj.working_time_start = this.termsFirstStepForm.controls.workingTimeStart.value;
          dayObj.working_time_end = this.termsFirstStepForm.controls.workingTimeEnd.value;
        } else {
          dayObj.working_time_start = this.termsFirstStepForm.controls.individualWorkingTimes.value[index].workingTimeStart;
          dayObj.working_time_end = this.termsFirstStepForm.controls.individualWorkingTimes.value[index].workingTimeEnd;
        }

        if (this.termsFirstStepForm.controls.everyDaySameSlots.value) {
          dayObj.break_time = this.termsFirstStepForm.controls.breakTime.value;
          dayObj.time_slot = this.termsFirstStepForm.controls.timeSlot.value;
        } else {
          dayObj.break_time = this.termsFirstStepForm.controls.individualWorkingSlotsAndBreaks.value[index].breakTime;
          dayObj.time_slot = this.termsFirstStepForm.controls.individualWorkingSlotsAndBreaks.value[index].timeSlot;
        }
        return dayObj;
      });
      this.businessProfilesHttpService.postTermsFirstStep(this.urlParams, bodyParams).then(res => {
        this.isEditTermsReq = true;
        this.submitForm.emit();
        this.toastrService.success(res.message);
        setTimeout(() => {
          this.isEditTermsReq = false;
        }, 2000);
      });
    }
  }
}

export default TermsFirstStepComponent
